import { BASE64URL_32_BYTES_PATTERN, lowerHostSchema } from "./kernel-wire-internal.js";
import { z } from "zod";
//#region src/kernel/kernel-handoff.ts
/**
* The kernel handoff wire: how a shell on a served host gets a session from the kernel origin
* (kernel apps, WS-1b, R-003, R-014, R-042, R-058, R-064).
*
* The flow, one PKCE S256 code per sign-in:
*
* 1. The shell on `<host>` (WS-16) makes a verifier, its challenge and a state. It sends the browser
*    to `https://kernel.<dp>` {@link KERNEL_AUTHORIZE_PATH} with the {@link AUTHORIZE_PARAMS}.
*    `return_host` names the host that waits for the code.
* 2. The kernel page (WS-15) parses the query with {@link kernelAuthorizeQuerySchema}. For a host
*    other than the canonical `<dp>` it shows the host and waits for a confirm click (R-064). It
*    then sends {@link KernelAuthorizeRequest} to core-api {@link KERNEL_AUTHORIZE_ROUTE}.
* 3. core-api (WS-11) accepts `returnHost` only when it is a served host of the org (R-014, R-058),
*    and answers a {@link KernelHandoffGrant} that lives {@link KERNEL_HANDOFF_CODE_TTL_SECONDS}.
* 4. The kernel page sends the browser to `https://<returnHost>` {@link SHELL_KERNEL_CALLBACK_PATH}
*    with the {@link CALLBACK_PARAMS}.
* 5. The shell parses the query with {@link kernelCallbackQuerySchema} and sends
*    {@link KernelTokenRequest} to {@link KERNEL_TOKEN_ROUTE} on its own host.
*
* No name of this module carries a redirect URI. The one host on the wire is `returnHost`, and the
* server checks it. Browser-safe: no Node builtin and no Node global. No schema uses `.transform()`.
*/
/** The kernel shell page that starts a handoff, on `https://kernel.<dp>`. */
const KERNEL_AUTHORIZE_PATH = "/authorize";
/** The core-api route that issues a handoff code, called from the kernel origin. */
const KERNEL_AUTHORIZE_ROUTE = "/v1/auth/kernel/authorize";
/** The core-api route that redeems a handoff code, called from the shell host. */
const KERNEL_TOKEN_ROUTE = "/v1/auth/kernel/token";
/** The shell page that receives the code, on the return host. It sits under the reserved `auth` segment. */
const SHELL_KERNEL_CALLBACK_PATH = "/auth/kernel-callback";
/** How long a handoff code lives: 60 s. One use. */
const KERNEL_HANDOFF_CODE_TTL_SECONDS = 60;
/** The longest `return_to` path, in characters. */
const KERNEL_RETURN_PATH_MAX_LENGTH = 2048;
/** The query names of the kernel authorize page, each once, and no other name. */
const AUTHORIZE_PARAMS = [
	"code_challenge",
	"code_challenge_method",
	"state",
	"return_to",
	"return_host"
];
/** The query names of the shell callback page, each once, and no other name. */
const CALLBACK_PARAMS = [
	"code",
	"state",
	"return_to"
];
/** A PKCE S256 code challenge: the 43 base64url characters of a SHA-256 (RFC 7636 section 4.2). */
const PKCE_CODE_CHALLENGE_PATTERN = BASE64URL_32_BYTES_PATTERN;
/** A PKCE code verifier: 43 to 128 unreserved characters (RFC 7636 section 4.1). */
const PKCE_CODE_VERIFIER_PATTERN = /^[A-Za-z0-9._~-]{43,128}$/;
/** A handoff code: the 43 base64url characters of 32 random bytes. */
const KERNEL_HANDOFF_CODE_PATTERN = BASE64URL_32_BYTES_PATTERN;
/** A handoff state: 16 to 128 base64url characters. */
const KERNEL_HANDOFF_STATE_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
/**
* The structural rule of a `return_to` path: it starts with `/`, its second character is not `/` or
* `\`, and every character is printable ASCII (`!` to `~`). So no tab, line break or space can hide
* a second `/`. The shell still runs `resolvePostLoginPath` on the value before it navigates.
*/
const KERNEL_RETURN_PATH_PATTERN = /^\/(?:[\x21-\x2e\x30-\x5b\x5d-\x7e][\x21-\x7e]*)?$/;
const codeChallengeSchema = z.string().length(43).regex(PKCE_CODE_CHALLENGE_PATTERN, "a PKCE S256 challenge");
const codeVerifierSchema = z.string().min(43).max(128).regex(PKCE_CODE_VERIFIER_PATTERN, "a PKCE code verifier");
const handoffCodeSchema = z.string().length(43).regex(KERNEL_HANDOFF_CODE_PATTERN, "a handoff code");
const handoffStateSchema = z.string().min(16).max(128).regex(KERNEL_HANDOFF_STATE_PATTERN, "a handoff state");
const returnPathSchema = z.string().min(1).max(KERNEL_RETURN_PATH_MAX_LENGTH).regex(KERNEL_RETURN_PATH_PATTERN, "a same-origin path");
/**
* The strict parse of {@link KernelAuthorizeQuery}. It checks the values only; the caller checks
* first that each name of {@link AUTHORIZE_PARAMS} occurs once, because a parsed search object
* keeps one value per name.
*/
const kernelAuthorizeQuerySchema = z.object({
	code_challenge: codeChallengeSchema,
	code_challenge_method: z.literal("S256"),
	state: handoffStateSchema,
	return_to: returnPathSchema,
	return_host: lowerHostSchema
}).strict();
/** The strict parse of {@link KernelCallbackQuery}. The caller checks first that each name occurs once. */
const kernelCallbackQuerySchema = z.object({
	code: handoffCodeSchema,
	state: handoffStateSchema,
	return_to: returnPathSchema
}).strict();
/** The strict parse of {@link KernelAuthorizeRequest}. */
const kernelAuthorizeRequestSchema = z.object({
	codeChallenge: codeChallengeSchema,
	codeChallengeMethod: z.literal("S256"),
	returnHost: lowerHostSchema
}).strict();
/** The strict parse of {@link KernelHandoffGrant}. */
const kernelHandoffGrantSchema = z.object({
	code: handoffCodeSchema,
	returnHost: lowerHostSchema,
	expiresInSeconds: z.literal(60)
}).strict();
/** The strict parse of {@link KernelTokenRequest}: `{ code, codeVerifier }` and no other member. */
const kernelTokenRequestSchema = z.object({
	code: handoffCodeSchema,
	codeVerifier: codeVerifierSchema
}).strict();
//#endregion
export { AUTHORIZE_PARAMS, CALLBACK_PARAMS, KERNEL_AUTHORIZE_PATH, KERNEL_AUTHORIZE_ROUTE, KERNEL_HANDOFF_CODE_PATTERN, KERNEL_HANDOFF_CODE_TTL_SECONDS, KERNEL_HANDOFF_STATE_PATTERN, KERNEL_RETURN_PATH_MAX_LENGTH, KERNEL_RETURN_PATH_PATTERN, KERNEL_TOKEN_ROUTE, PKCE_CODE_CHALLENGE_PATTERN, PKCE_CODE_VERIFIER_PATTERN, SHELL_KERNEL_CALLBACK_PATH, kernelAuthorizeQuerySchema, kernelAuthorizeRequestSchema, kernelCallbackQuerySchema, kernelHandoffGrantSchema, kernelTokenRequestSchema };

//# sourceMappingURL=kernel-handoff.js.map